<template>
  <div>
    <div v-if="loading">
      <loading-spinner />
    </div>
    <div v-if="!loading">
      <b-breadcrumb :items="navigationItems" />
      <b-modal
        id="delete-payment"
        ref="deletePayment"
        title="Delete Alternative Payment Configuration"
        @ok="handleDeleteOK"
        @hidden="resetModal ('delete-payment')"
      >
        <p>Are you sure you want to delete : <b>{{ payment.name }}</b></p>
      </b-modal>
      <b-container>
        <b-row align-v="center">
          <b-col
            cols="6"
            class="pl-0"
          >
            <h2 style="display:inline">
              Alternative Payments for {{ (clientName.text) ? clientName.text : "Please select a Publisher" }}
            </h2>
          </b-col>
        </b-row>
        <b-row align-v="center">
          <b-col
            cols="6"
            class="form-row"
          >
            <router-link
              class="navigation-links"
              :to="{name: 'configuration', params: { clientId: clientId }}"
            >
              View Configurations
            </router-link>
          </b-col>
          <b-col
            cols="6"
            class="form-row"
            align-h="end"
          >
            <b-col
              cols="5"
              class="text-right align-self-center"
            >
              <label
                for="table-search"
              >Search:</label>
            </b-col>
            <b-col cols="7">
              <b-form-input
                id="table-search"
                v-model="search"
                type="text"
              />
            </b-col>
          </b-col>
        </b-row>
        <br>
        <b-row>
          <b-table
            v-b-scrollspy
            outlined
            striped
            hover
            :items="items"
            head-variant="light"
            :fields="computeTableFields"
            :current-page="currentPage"
            show-empty
            empty-text="No alternative payments created for selected publisher."
            :per-page="perPage"
            :filter="search"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :sort-compare="sortCompareForPayments"
          >
            <template v-slot:cell(paymentId)="data">
              <router-link
                class="navigation-links"
                :to="{name: 'editAlternativePayment', params: { clientId: clientId, paymentType: data.item.processor, paymentId: data.item.paymentId }}"
              >
                {{ data.item.paymentId }}
              </router-link>
            </template>
            <template v-slot:cell(processor)="data">
              <img
                :src="'/static/images/logos/' + logos[data.item.processor]"
                height="40"
                alt="data.item.processor"
              >
            </template>
            <template v-slot:cell(expiresAt)="data">
              <div v-if="data.item.expiresAt">
                {{ data.item.expiresAt.toLocaleDateString() }} {{ data.item.expiresAt.toLocaleTimeString() }}
              </div>
            </template>
            <template v-slot:cell(action)="data">
              <b-link
                v-if="!data.item.expiresAt"
                v-b-modal="'delete-payment'"
                class="navigation-links"
                @click="updateDeletedItem(data.item)"
              >
                Delete
              </b-link>
              <b-link
                v-if="data.item.expiresAt"
                class="navigation-links"
                @click="restorePayment(data.item)"
              >
                Restore
              </b-link>
            </template>
          </b-table>
        </b-row>
        <b-row>
          <b-col v-show="showPagination">
            <b-pagination
              v-model="currentPage"
              :total-rows="totalRows"
              :per-page="perPage"
              align="center"
            />
          </b-col>
        </b-row>
        <b-modal
          id="new-payment"
          ref="new-payment"
          title="Create Alternative Payment"
          @show="resetModal('new-payment')"
          @hidden="resetModal('new-payment')"
          @ok="handleOk"
        >
          <p>This will create a new alternative payment with a randomly generated unique ID.</p>
          <form
            ref="form"
            @submit.stop.prevent="handleSubmit"
          >
            <b-form-group
              label="Name"
              label-for="name-input"
              invalid-feedback="Name is required"
            >
              <b-form-input
                id="name-input"
                v-model="$v.form.name.$model"
                :state="validateState('name')"
              />
            </b-form-group>
            <b-form-group
              label="Type"
              label-for="payment-type-select"
              invalid-feedback="Payment type is required"
            >
              <b-form-select
                id="payment-type-select"
                v-model="$v.form.paymentType.$model"
                :options="paymentTypes"
                :state="validateState('paymentType')"
              />
            </b-form-group>
          </form>
        </b-modal>
        <div class="button-action-ct">
          <b-button
            v-b-modal.new-payment
            variant="primary"
          >
            New Alternative Payment
          </b-button>
        </div>
      </b-container>
    </div>
  </div>
</template>
<script>
import { dataAPI } from '../services/DataAPI'
import LoadingSpinner from '../UIComponents/LoadingSpinner'
import { validationMixin } from 'vuelidate'
import { required } from 'vuelidate/lib/validators'

export default {
  name: 'AlternativePayments',
  components: {
    LoadingSpinner
  },
  mixins: [validationMixin],
  data() {
    return {
      form: { name: null, paymentType: null },
      sortBy: 'processor',
      sortDesc: true,
      payment: { name: null, paymentId: null },
      fields: [
        {
          key: 'processor',
          label: 'Processor',
          sortable: true
        },
        {
          key: 'paymentId',
          label: 'Payment ID',
          sortable: true
        },
        {
          key: 'name',
          label: 'Name',
          sortable: true
        },
        {
          key: 'expiresAt',
          label: 'Expiration',
          sortable: true
        },
        {
          key: 'action',
          label: 'Actions'
        }
      ],
      paymentTypes: [
        { value: 'AMAZON', text: 'Amazon Pay' },
        { value: 'APPLE', text: 'Apple Pay' },
        { value: 'PAYPAL', text: 'PayPal' }
      ],
      search: null,
      items: [],
      showExpiring: true,
      loading: true,
      perPage: 10,
      currentPage: 1,
      totalRows: 0,
      navigationItems: [{
        text: 'Home',
        to: { name: 'home' }
      }, {
        text: 'Payments',
        to: { name: 'client' }
      }, {
        text: 'Alternative Payments',
        active: true
      }],
      clientId: this.$route.params.clientId,
      logos: {
        APPLE: 'apple-pay.png',
        PAYPAL: 'pay-pal.png',
        AMAZON: 'amazon-pay.png'
      }
    }
  },
  validations: {
    form: {
      name: {
        required
      },
      paymentType: {
        required
      }
    }
  },
  computed: {
    currentUserToken() {
      return this.$store.state.auth.userToken
    },
    showPagination() {
      return (this.totalRows > this.perPage)
    },
    clientName() {
      return this.$store.state.client.clientSelected
    },
    computeTableFields() {
      if (this.showExpiring) {
        return this.fields
      }
      return this.fields.filter(f => f.key !== 'expiresAt')
    }
  },
  created() {
    this.fetchAlternativePayments()
  },
  methods: {
    updateDeletedItem (item) {
      if (item) {
        this.payment = item
      }
    },
    restorePayment(payment) {
      return dataAPI(this.currentUserToken).restorePayment(payment, this.clientId)
        .then(() => {
          this.fetchAlternativePayments()
        })
    },
    hideDeleteModal() {
      this.$refs.deletePayment.hide()
    },
    sortCompareForPayments(a, b, key) {
      if (key === 'paymentId') {
        return a.paymentId - b.paymentId
      } else if (key === 'processor') {
        return a.processor - b.processor
      } else if (key === 'name') {
        return a.name - b.name
      }
    },
    validateState(name) {
      const { $dirty, $error } = this.$v.form[name]
      return $dirty ? !$error : null
    },
    resetModal(id) {
      if (id === 'delete-payment') {
        this.payment = {
          name: null,
          paymentId: null
        }
      } else {
        this.form = {
          name: null,
          paymentType: null
        }
      }
      this.$nextTick(() => {
        this.$v.$reset()
      })
    },
    handleDeleteOK() {
      this.handleDeleteSubmit()
    },
    handleOk(bvModalEvt) {
      // Prevent modal from closing
      bvModalEvt.preventDefault()
      // Trigger submit handler
      this.handleSubmit()
    },
    handleSubmit() {
      // Exit when the form isn't valid
      this.$v.form.$touch()
      if (this.$v.form.$anyError) {
        return
      }
      const vm = this
      // Push the name to submitted names
      return dataAPI(this.currentUserToken)
        .createAlternativePayment({
          name: this.$v.form.name.$model,
          processor: this.$v.form.paymentType.$model,
          clientId: this.clientId
        })
        .then((data) => {
          return vm.$router.push({
            name: 'editAlternativePayment',
            params: {
              clientId: vm.$route.params.clientId,
              paymentId: data.paymentId,
              paymentType: data.processor
            }
          })
        })
    },
    handleDeleteSubmit() {
      return dataAPI(this.currentUserToken)
        .deletePayment(this.payment, this.clientId)
        .then((data) => {
          this.resetModal('delete-payment')
          this.hideDeleteModal()
          this.fetchAlternativePayments()
        }).catch(function (error) {
          console.error('Error occurred deleting  alternative payment', error)
          this.error = `Error deleting alternative payment for ${this.payment.paymentId}`
        })
    },
    fetchAlternativePayments() {
      dataAPI(this.currentUserToken)
        .getPaymentsByClientId(this.clientId)
        .then((data) => {
          this.loading = false
          this.items = data
          this.totalRows = data.length
        })
        .catch((error) => {
          console.error('Error loading alternative payments: ', error)
          this.error = `Error loading page for ${this.clientId}`
        })
    }
  }
}
</script>
